import { useState, useEffect } from 'react'
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  Plus,
  Sparkles,
  Trash2,
  ImageDown,
  Wand2,
  GripVertical,
} from 'lucide-react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  VisuallyHidden,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import type { Note, NoteImage } from '@/types'
import { InlineEdit } from './InlineEdit'
import { MaskPainter } from '../MaskPainter'

// Zustand Store
import { useGenerationStore } from '@/stores/generationStore'

type RedrawKey = `${string}-${number}`

export interface NoteDetailDialogProps {
  note: Note | null
  onClose: () => void
  currentNoteIndex: number
  totalNotes: number
  onPrevNote: () => void
  onNextNote: () => void
  onSmartImages: (note: Note) => void
  onOpenAssetPicker: (noteId: string, index: number) => void
  onDeleteNote: (noteId: string) => void
  onUpdateText: (noteId: string, field: 'title' | 'content', value: string) => void
  onUpdateHashtags: (noteId: string, hashtags: string[]) => void
  onAiRedraw: (note: Note, index: number, maskBase64?: string, promptOverride?: string) => void
  onUpdateImages: (noteId: string, images: NoteImage[]) => void
  onAbort?: (noteId: string) => void
}

/**
 * 排序项组件
 */
function SortableImageItem({ 
  image, 
  index, 
  isActive, 
  onClick,
  onDownload,
  onAiRedraw,
  onDelete,
  isRedrawing,
}: { 
  image: NoteImage; 
  index: number; 
  isActive: boolean; 
  onClick: () => void;
  onDownload: () => void;
  onAiRedraw: () => void;
  onDelete: () => void;
  isRedrawing: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: image.id || `img-${index}` })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 0,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "group relative flex items-center gap-3 p-2 rounded-lg border transition-all cursor-pointer",
        isActive ? "bg-primary/5 border-primary" : "bg-white border-transparent hover:bg-gray-50",
        isDragging && "opacity-50 shadow-lg"
      )}
      onClick={onClick}
    >
      <div 
        {...attributes} 
        {...listeners} 
        className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
      >
        <GripVertical className="w-4 h-4" />
      </div>
      
      <div className="relative w-16 h-20 rounded bg-gray-100 overflow-hidden shrink-0">
        {image.url ? (
          <img src={image.url} className="w-full h-full object-cover" alt="" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
          </div>
        )}
        <div className="absolute top-0 left-0 bg-black/50 text-white text-[10px] px-1 rounded-br">
          {index + 1}
        </div>
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium truncate text-gray-700">
          {image.image_text || (image.status === 'completed' ? '已就绪' : '处理中...')}
        </p>
      </div>

      {/* 操作按钮 - 仅在非拖拽时显示 */}
      {!isDragging && (
        <div className={cn(
          "flex flex-col gap-1 transition-opacity",
          isActive ? "opacity-100" : "opacity-0 group-hover:opacity-100"
        )}>
          {image.status === 'completed' && (
            <>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 rounded-full hover:bg-zinc-100"
                onClick={(e) => {
                  e.stopPropagation();
                  onDownload();
                }}
                title="下载图片"
              >
                <ImageDown className="w-3.5 h-3.5" />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className={cn(
                  "h-7 w-7 rounded-full hover:bg-zinc-100",
                  isRedrawing && "animate-pulse"
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  onAiRedraw();
                }}
                disabled={isRedrawing}
                title="AI 修图"
              >
                <Wand2 className={cn("w-3.5 h-3.5", isRedrawing ? "text-gray-400" : "text-pink-500")} />
              </Button>
            </>
          )}
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7 rounded-full hover:bg-red-50 text-red-400 hover:text-red-500"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            title="删除此图"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      )}
    </div>
  )
}

/**
 * 笔记详情弹窗组件
 * 模拟手机全屏浏览，右侧增加配图管理侧边栏
 */
export function NoteDetailDialog({
  note,
  onClose,
  currentNoteIndex,
  totalNotes,
  onPrevNote,
  onNextNote,
  onSmartImages,
  onOpenAssetPicker,
  onDeleteNote,
  onUpdateText,
  onUpdateHashtags,
  onAiRedraw,
  onUpdateImages,
  onAbort,
}: NoteDetailDialogProps) {
  // ========== 本地状态 ==========
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [maskPainterOpen, setMaskPainterOpen] = useState(false)

  // ========== DnD Sensors ==========
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // ========== 从 Store 订阅状态 ==========
  // 优化：精细化订阅，避免无关笔记更新导致详情页重绘
  const currentInitialPrompt = useGenerationStore((state) => 
    note ? (state.redrawPrompt[`${note.id}-${currentImageIndex}`] || '') : ''
  )
  const isSmartLoading = useGenerationStore((state) => 
    note ? (state.smartLoading[note.id] || false) : false
  )
  
  const setRedrawPrompt = useGenerationStore((state) => state.setRedrawPrompt)
  const redrawLoadingMap = useGenerationStore((state) => state.redrawLoading)

  // ========== 笔记切换时重置图片索引 ==========
  useEffect(() => {
    setCurrentImageIndex(0)
  }, [note?.id])

  // ========== 键盘快捷键 ==========
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!note || maskPainterOpen) return
      
      // 仅在没有聚焦到输入框时触发
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setCurrentImageIndex(prev => 
          prev > 0 ? prev - 1 : note.images.length - 1
        )
      } else if (e.key === 'ArrowDown') {
        e.preventDefault()
        setCurrentImageIndex(prev => 
          prev < note.images.length - 1 ? prev + 1 : 0
        )
      } else if (e.key === 'ArrowLeft') {
        if (currentNoteIndex > 0) {
          handleNoteChange('prev')
        }
      } else if (e.key === 'ArrowRight') {
        if (currentNoteIndex < totalNotes - 1) {
          handleNoteChange('next')
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [note, currentNoteIndex, totalNotes, maskPainterOpen])

  const handleNoteChange = (direction: 'prev' | 'next') => {
    setCurrentImageIndex(0)
    if (direction === 'prev') {
      onPrevNote()
    } else {
      onNextNote()
    }
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!note || !over || active.id === over.id) return

    const oldIndex = note.images.findIndex(img => (img.id || `img-${note.images.indexOf(img)}`) === active.id)
    const newIndex = note.images.findIndex(img => (img.id || `img-${note.images.indexOf(img)}`) === over.id)

    if (oldIndex === -1 || newIndex === -1) return

    const newImages = arrayMove(note.images, oldIndex, newIndex).map((img, idx) => ({
      ...img,
      index: idx
    }))

    onUpdateImages(note.id, newImages)
    
    // 如果移动的是当前显示的图片，更新索引
    if (oldIndex === currentImageIndex) {
      setCurrentImageIndex(newIndex)
    } else if (currentImageIndex > oldIndex && currentImageIndex <= newIndex) {
      setCurrentImageIndex(prev => prev - 1)
    } else if (currentImageIndex < oldIndex && currentImageIndex >= newIndex) {
      setCurrentImageIndex(prev => prev + 1)
    }
  }

  if (!note) return null

  const isProcessing =
    isSmartLoading ||
    note.status === 'planning' ||
    note.status === 'generating'

  const currentImage = note.images[currentImageIndex]
  const redrawKey: RedrawKey = `${note.id}-${currentImageIndex}`

  return (
    <Dialog open={!!note} onOpenChange={open => !open && onClose()}>
      <DialogContent
        className="fixed inset-0 w-screen h-screen max-w-none sm:max-w-none translate-x-0 translate-y-0 p-0 bg-zinc-900 border-none shadow-none flex items-stretch outline-none z-50 rounded-none"
        showCloseButton={false}
        aria-describedby={undefined}
      >
        <VisuallyHidden>
          <DialogTitle>笔记详情预览</DialogTitle>
        </VisuallyHidden>

        <div className="flex w-full h-full items-stretch overflow-hidden">
          {/* 左侧：手机预览区 */}
          <div className="flex-1 relative flex items-center justify-center bg-black/20 p-8">
            {/* 关闭按钮 */}
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-6 left-6 text-white/70 hover:text-white hover:bg-white/10 z-50 rounded-full w-12 h-12"
              onClick={onClose}
            >
              <ChevronLeft className="w-8 h-8" />
            </Button>

            {/* 上一篇笔记按钮 */}
            {currentNoteIndex > 0 && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute left-8 top-1/2 -translate-y-1/2 text-white/80 hover:text-white transition-colors z-50 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full w-12 h-12"
                onClick={() => handleNoteChange('prev')}
              >
                <ChevronLeft className="w-10 h-10" />
              </Button>
            )}

            {/* 下一篇笔记按钮 */}
            {currentNoteIndex < totalNotes - 1 && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-8 top-1/2 -translate-y-1/2 text-white/80 hover:text-white transition-colors z-50 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full w-12 h-12"
                onClick={() => handleNoteChange('next')}
              >
                <ChevronRight className="w-10 h-10" />
              </Button>
            )}

            {/* 手机容器 */}
            <div className="relative w-[375px] h-[812px] max-h-[90vh] bg-white rounded-[40px] shadow-2xl overflow-hidden flex flex-col shrink-0 border-[8px] border-zinc-800">
              {/* 可滚动内容区域 */}
              <ScrollArea className="flex-1 w-full bg-white scrollbar-hide">
                <div className="pb-20">
                  {/* 配图轮播 */}
                  <div className="relative w-full aspect-[3/4] bg-zinc-100 group">
                    {note.images.length > 0 ? (
                      <>
                        {currentImage?.status === 'completed' &&
                        currentImage?.url ? (
                          <img
                            src={currentImage.url}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground gap-2">
                            <Loader2 className="w-6 h-6 animate-spin text-primary" />
                            <span className="text-sm">
                              {currentImage?.status === 'failed'
                                ? '生成失败，请重试'
                                : '正在生成'}
                            </span>
                          </div>
                        )}

                        {/* 切换按钮 */}
                        {note.images.length > 1 && (
                          <>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full text-white/90 hover:text-white bg-white/20 hover:bg-white/30 backdrop-blur-sm transition-all z-10"
                              onClick={e => {
                                e.stopPropagation()
                                setCurrentImageIndex(prev =>
                                  prev > 0 ? prev - 1 : note.images.length - 1
                                )
                              }}
                            >
                              <ChevronLeft className="w-8 h-8 shadow-sm" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full text-white/90 hover:text-white bg-white/20 hover:bg-white/30 backdrop-blur-sm transition-all z-10"
                              onClick={e => {
                                e.stopPropagation()
                                setCurrentImageIndex(prev =>
                                  prev < note.images.length - 1 ? prev + 1 : 0
                                )
                              }}
                            >
                              <ChevronRight className="w-8 h-8 shadow-sm" />
                            </Button>
                            {/* 指示点 */}
                            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 p-1 bg-black/20 rounded-full backdrop-blur-[2px]">
                              {note.images.map((_, idx) => (
                                <div
                                  key={idx}
                                  className={cn(
                                    'w-1.5 h-1.5 rounded-full transition-colors',
                                    idx === currentImageIndex
                                      ? 'bg-white'
                                      : 'bg-white/40'
                                  )}
                                />
                              ))}
                            </div>
                          </>
                        )}
                      </>
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground gap-2">
                        <span>暂无配图</span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onOpenAssetPicker(note.id, 0)}
                        >
                          <Plus className="w-4 h-4 mr-1" /> 添加图片
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* 笔记内容 */}
                  <div className="p-5 space-y-4">
                    {/* 标题 */}
                    <div className="group relative">
                      <InlineEdit
                        value={note.title}
                        onSave={val => onUpdateText(note.id, 'title', val)}
                        className="text-xl font-bold text-gray-900 leading-snug break-words"
                      />
                    </div>

                    {/* 正文 */}
                    <div className="group relative min-h-[100px]">
                      <InlineEdit
                        value={note.content}
                        onSave={val => onUpdateText(note.id, 'content', val)}
                        textarea
                        className="text-[15px] text-gray-800 leading-relaxed whitespace-pre-wrap break-words"
                      />
                    </div>

                    {/* 标签 */}
                    <div className="pt-2 border-t border-dashed">
                      <Label className="text-xs text-muted-foreground mb-2 block">
                        话题标签 (点击修改)
                      </Label>
                      <InlineEdit
                        value={note.hashtags.map(t => '#' + t).join(' ')}
                        onSave={val => {
                          const newTags = val
                            .split(/[\s,]+/)
                            .map(t => t.replace(/^#/, ''))
                            .filter(Boolean)
                          onUpdateHashtags(note.id, newTags)
                        }}
                        className="text-blue-900 text-[15px] leading-relaxed break-words"
                      />
                    </div>

                    <div className="text-xs text-gray-400 mt-4 pb-4">
                      {new Date(note.created_at).toLocaleString()} ·{' '}
                      {note.status === 'completed'
                        ? '已生成'
                        : note.status === 'generating'
                          ? '正在处理配图'
                          : note.status === 'planning'
                            ? '正在生成方案'
                            : '草稿'}
                    </div>
                  </div>
                </div>
              </ScrollArea>
            </div>
          </div>

          {/* 右侧：配图管理侧边栏 */}
          <div className="w-[320px] bg-white border-l flex flex-col shrink-0">
            <div className="p-6 border-b flex items-start justify-between">
              <div className="flex-1">
                <h2 className="text-lg font-bold flex items-center gap-2">
                  配图管理
                  <span className="text-xs font-normal text-muted-foreground bg-gray-100 px-2 py-0.5 rounded-full">
                    {note.images.length}
                  </span>
                </h2>
                <p className="text-xs text-muted-foreground mt-1">拖拽缩略图可调整配图顺序</p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="text-gray-400 hover:text-red-500 hover:bg-red-50 -mr-2 -mt-1"
                onClick={() => onDeleteNote(note.id)}
                title="删除笔记"
              >
                <Trash2 className="w-5 h-5" />
              </Button>
            </div>

            {/* 操作按钮区 */}
            <div className="p-4 flex flex-col gap-2 border-b bg-gray-50/50">
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  className="h-10 bg-pink-500 text-white hover:bg-pink-600 border-none flex-1"
                  onClick={() => onSmartImages(note)}
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2" />
                  )}
                  智能配图
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-10 border-dashed"
                  onClick={() => onOpenAssetPicker(note.id, note.images.length)}
                  disabled={isProcessing}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  添加图片
                </Button>
              </div>

              {isProcessing && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9 w-full border-red-200 text-red-500 hover:bg-red-50 hover:text-red-600"
                  onClick={() => onAbort?.(note.id)}
                >
                  中止当前任务
                </Button>
              )}
            </div>

            {/* 配图列表区 */}
            <ScrollArea className="flex-1">
              <div className="p-4">
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={note.images.map((img, i) => img.id || `img-${i}`)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-3">
                      {note.images.map((img, idx) => {
                        const itemRedrawKey: RedrawKey = `${note.id}-${idx}`
                        const itemIsRedrawing = redrawLoadingMap[itemRedrawKey] || false
                        
                        return (
                          <SortableImageItem
                            key={`img-${idx}`}
                            image={img}
                            index={idx}
                            isActive={idx === currentImageIndex}
                            onClick={() => setCurrentImageIndex(idx)}
                            onDownload={() => {
                              if (img.url) {
                                const link = document.createElement('a')
                                link.href = img.url
                                link.download = `note-${note.id}-image-${idx + 1}.png`
                                link.target = '_blank'
                                link.click()
                              }
                            }}
                            onAiRedraw={() => {
                              setCurrentImageIndex(idx)
                              setMaskPainterOpen(true)
                            }}
                            onDelete={() => {
                              const newImages = note.images
                                .filter((_, i) => i !== idx)
                                .map((img, i) => ({ ...img, index: i }))
                              onUpdateImages(note.id, newImages)
                              if (currentImageIndex >= newImages.length) {
                                setCurrentImageIndex(Math.max(0, newImages.length - 1))
                              }
                            }}
                            isRedrawing={itemIsRedrawing}
                          />
                        )
                      })}
                      {note.images.length === 0 && (
                        <div className="py-10 text-center text-sm text-gray-400">
                          暂无配图，请点击上方按钮添加
                        </div>
                      )}
                    </div>
                  </SortableContext>
                </DndContext>
              </div>
            </ScrollArea>
          </div>
        </div>
      </DialogContent>

      {/* 遮罩绘制弹窗 */}
      {currentImage?.url && (
        <MaskPainter
          open={maskPainterOpen}
          imageUrl={currentImage.url}
          initialPrompt={currentInitialPrompt}
          onConfirm={({ compositeBase64, prompt, mode }) => {
            setMaskPainterOpen(false)
            // 保存指令到 Store
            setRedrawPrompt(redrawKey, prompt)
            // 执行 AI 重绘
            onAiRedraw(note, currentImageIndex, mode === 'inpaint' ? compositeBase64 : undefined, prompt)
          }}
          onCancel={() => setMaskPainterOpen(false)}
        />
      )}
    </Dialog>
  )
}
